/**
 * Shared SSE (Server-Sent Events) service.
 *
 * Maintains a single EventSource connection for ticket updates,
 * shared across all subscribers. Uses exponential backoff on errors
 * to prevent connection storms that exhaust ephemeral ports.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL : 'http://localhost:8080';

let eventSource = null;
let listeners = new Set();
let retryTimeout = null;
let retryDelay = 1000; // start at 1s
const MAX_RETRY_DELAY = 30000; // cap at 30s

function connect() {
  if (eventSource) return;

  eventSource = new EventSource(`${API_BASE_URL}/api/events/tickets`);

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      listeners.forEach((cb) => cb(data));
    } catch (err) {
      console.error('Failed to parse SSE event:', err);
    }
  };

  eventSource.onopen = () => {
    // Reset backoff on successful connection
    retryDelay = 1000;
  };

  eventSource.onerror = () => {
    // Close the broken connection and reconnect with backoff
    disconnect(false);

    retryTimeout = setTimeout(() => {
      retryTimeout = null;
      if (listeners.size > 0) {
        connect();
      }
    }, retryDelay);

    // Exponential backoff
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
  };
}

function disconnect(clearRetry = true) {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  if (clearRetry && retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = null;
    retryDelay = 1000;
  }
}

/**
 * Subscribe to SSE ticket updates.
 * Returns an unsubscribe function.
 */
export function subscribeTicketUpdates(callback) {
  listeners.add(callback);

  // Start connection if this is the first subscriber
  if (listeners.size === 1) {
    connect();
  }

  return () => {
    listeners.delete(callback);
    // Disconnect if no subscribers remain
    if (listeners.size === 0) {
      disconnect();
    }
  };
}
